import { ChangeUserStatusSchema, type ChangeUserStatusRequest } from '@aysar/contracts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { type ModalComponentProps } from '@/shared/components/Modals/modal-registry/modal-registry.types';
import { DynamicFormModal } from '@/shared/components/dynamic-form/DynamicFormModal/DynamicFormModal';
import { useToast } from '@/shared/hooks/useToast/useToast';

import {
  useAccountLifecycleQuery,
  useChangeAccountStatusMutation,
} from '@/shared/services/account-status/account-status.api';
import { statusTargets } from '@/shared/utils/status-targets/status-targets.util';

import { buildChangeStatusFields } from '../config/account-actions.config';

/**
 * The generic lifecycle move (N9) — everything suspend and ban are not.
 *
 * A FORM rather than a confirm dialog, because the operator is choosing *which*
 * state and each choice has a different consequence for the member. Suspend and
 * ban stay their own doors: one takes a mandatory end date, the other a
 * member-facing reason, and neither is reachable through this route.
 *
 * **It reads the account itself.** `ModalData` is ids-only by design, and the
 * offered targets depend on where the account is *now* — so the current status
 * comes from the shared account api's one read rather than travelling in the
 * payload, where it could be stale by the time the dialog opens.
 *
 * The list it offers is `statusTargets`, not the raw transition map: a suspended
 * account has five map entries and zero reachable targets, because the server
 * refuses to leave suspension by any route but `restore`.
 */
export function ChangeStatusModal({ open, onCancel, modalData }: ModalComponentProps) {
  const { t } = useTranslation('uiKit');
  const toast = useToast();
  const [changeStatus] = useChangeAccountStatusMutation();

  const userId = typeof modalData?.['userId'] === 'string' ? modalData['userId'] : null;
  const email = typeof modalData?.['email'] === 'string' ? modalData['email'] : '';

  const detail = useAccountLifecycleQuery(userId ?? '', { skip: userId === null });
  const status = detail.data?.status;

  const targets = useMemo(() => (status === undefined ? [] : statusTargets(status)), [status]);
  const fields = useMemo(() => buildChangeStatusFields(t, targets), [t, targets]);

  async function submit(values: ChangeUserStatusRequest): Promise<void> {
    if (userId === null) {
      return;
    }

    await changeStatus({ id: userId, body: values }).unwrap();
    toast.success(t('account.changeStatus.success'));
    onCancel();
  }

  return (
    <DynamicFormModal<ChangeUserStatusRequest>
      isOpen={open}
      onOpenChange={(next) => {
        if (!next) {
          onCancel();
        }
      }}
      title={t('account.changeStatus.title', { email })}
      // Names where the account IS, so the offered moves read as a consequence
      // of that rather than as an arbitrary short list.
      description={t('account.changeStatus.description', {
        status: status === undefined ? '' : t(`account.status.${status}`),
      })}
      fields={fields}
      schema={ChangeUserStatusSchema}
      submitLabel={t('account.changeStatus.submit')}
      onSubmit={submit}
    />
  );
}
